// Écran 8 — Mes clés de sécurité (O100). Une écriture dans un outil du client n'est validée que par un geste physique de la
// personne (clé de sécurité WebAuthn). Cet écran : l'état annoncé par GET /v0/moi, l'enrôlement d'une clé (avec son code de
// vérification, à lire à un administrateur HORS de l'application), la liste des clés, la révocation (sans geste, F9), l'ajout
// d'une seconde clé avec une clé déjà active et, pour l'administration, l'approbation des clés des autres (geste de
// l'administrateur + saisie du code lu par la personne ; l'API ne rend jamais ce code à l'administrateur).
// Rien du geste n'est gardé : défi, options et réponse du navigateur restent dans des variables locales, le temps de l'appel.
// Aucun bouton de suppression : une clé se révoque (elle reste dans l'historique).
import { useQuery } from '@tanstack/react-query';
import { useId, useState, type FormEvent } from 'react';
import type { CleEnrolee, EtatCle, VueCle, VueCleAdmin } from '../api/types';
import { Chargement, Erreur, Etiquette } from '../composants/communs';
import { AdresseAlerte, AdressesDuClient } from './Adresses';
import { FormulaireMotif, MessageErreur, useAction, useRelireCles } from './ClesCommuns';
import { useEkera } from '../contexte';
import { dateFr } from '../domaine/format';
import {
  cleEnCarence,
  cleInutilisable,
  codeVerificationValide,
  etatAffiche,
  gesteAssertion,
  gesteCreation,
  indisponibiliteNavigateur,
  LIBELLE_VOIE_CLE,
  normaliserCodeVerification,
  resumeCles,
} from '../domaine/webauthn';

export function EcranCles() {
  const { moi, administrateur } = useEkera();
  const resume = resumeCles(moi, dateFr);
  const sansWebauthn = indisponibiliteNavigateur();
  return (
    <div className="pile">
      <section className="panneau" aria-labelledby="t-cles-principe">
        <h2 id="t-cles-principe">Une écriture dans vos outils se valide par un geste sur votre clé de sécurité</h2>
        <ol className="liste-etapes">
          <li>Vous enregistrez une <b>clé de sécurité</b> (clé matérielle certifiée, avec code ou empreinte). Ekera ne reçoit que sa partie publique.</li>
          <li>
            Un <b>administrateur</b> approuve la clé : vous lui lisez, <b>par téléphone ou en personne</b>, le code de vérification affiché ici.
            Ekera ne le lui montre pas : il doit le saisir.
          </li>
          <li>Après un <b>délai de sécurité</b>, la clé devient active. Pendant ce délai, vous ou un administrateur pouvez la révoquer.</li>
          <li>À chaque validation d’écriture, après la lecture du plan, vous touchez votre clé : sans ce geste, rien n’est signé.</li>
        </ol>
        <p className="note">
          Clé perdue ou volée ? Révoquez-la tout de suite (sans geste). Gardez de préférence <b>deux clés</b>, et au moins deux administrateurs avec une clé.
        </p>
      </section>

      <section className="panneau pile" aria-labelledby="t-cles-etat" data-testid="etat-cles">
        <h2 id="t-cles-etat">Votre situation</h2>
        <p className={`encadre ${resume.ton}`} role="status" data-testid="resume-cles">
          {resume.texte}
        </p>
        {resume.peutEnroler && sansWebauthn && (
          <p className="encadre erreur" role="alert" data-testid="webauthn-absent">
            {sansWebauthn}
          </p>
        )}
      </section>

      {resume.peutEnroler && (
        <>
          <AdresseAlerte />
          <Enrolement />
          <MesCles />
        </>
      )}
      {administrateur && moi.webauthn && <ClesDuClient />}
      {administrateur && moi.webauthn && <AdressesDuClient />}
    </div>
  );
}

// ---------------------------------------------------------------- enrôlement

function Enrolement() {
  const { client } = useEkera();
  const relire = useRelireCles();
  const { occupe, erreur, executer } = useAction();
  const [nom, setNom] = useState('Ma clé de sécurité');
  const [amorcage, setAmorcage] = useState('');
  const [fait, setFait] = useState<CleEnrolee | null>(null);
  const idNom = useId();
  const idCode = useId();
  const indispo = indisponibiliteNavigateur();

  async function enregistrer(e: FormEvent) {
    e.preventDefault();
    setFait(null);
    const r = await executer('enrolement', async () => {
      const demande = await client.optionsEnrolement(nom.trim());
      // le geste : la personne touche sa clé (le navigateur attend 60 s au plus)
      const reponse = await gesteCreation(demande.options);
      return client.enroler(demande.demande_id, reponse, amorcage.trim() || undefined);
    });
    if (r) {
      setFait(r);
      setAmorcage('');
      await relire();
    }
  }

  return (
    <section className="panneau pile" aria-labelledby="t-enrolement">
      <h2 id="t-enrolement">Enregistrer une clé de sécurité</h2>
      <form className="pile" style={{ gap: 10 }} onSubmit={(e) => void enregistrer(e)}>
        <div className="champ">
          <label htmlFor={idNom}>
            Nom de la clé <span className="aide">(1 à 60 caractères, pour la reconnaître : « clé du bureau », « clé de secours »)</span>
          </label>
          <input id={idNom} type="text" value={nom} maxLength={60} required onChange={(e) => setNom(e.target.value)} autoComplete="off" />
        </div>
        <div className="champ">
          <label htmlFor={idCode}>
            Code d’amorçage <span className="aide">(facultatif : seulement pour le premier administrateur de l’entreprise, code remis par Ekera hors de l’application)</span>
          </label>
          <input id={idCode} type="text" value={amorcage} maxLength={80} onChange={(e) => setAmorcage(e.target.value)} autoComplete="off" spellCheck={false} />
        </div>
        <div className="ligne">
          <button type="submit" className="btn principal" disabled={!!occupe || !!indispo || nom.trim().length === 0}>
            {occupe === 'enrolement' ? 'Touchez votre clé de sécurité…' : 'Enregistrer une clé de sécurité'}
          </button>
        </div>
        {occupe === 'enrolement' && (
          <p className="note" role="status">
            Le navigateur va vous demander de brancher ou de toucher votre clé, puis de saisir son code ou de poser votre doigt.
          </p>
        )}
      </form>
      <MessageErreur erreur={erreur} titre="La clé n’a pas été enregistrée." />
      {fait && (
        <div className="encadre ok pile" style={{ gap: 8 }} role="status" data-testid="cle-enrolee">
          <p>
            <b>Clé enregistrée.</b>{' '}
            {fait.etat === 'active'
              ? 'Elle est déjà active.'
              : fait.active_le
                ? `Elle sera active le ${dateFr(fait.active_le)} (délai de sécurité).`
                : 'Elle attend l’approbation d’un administrateur.'}
          </p>
          <p>
            Code de vérification à lire à un administrateur, <b>par téléphone ou en personne, jamais par messagerie ni courriel</b> :
          </p>
          <p className="num" style={{ fontSize: 28, letterSpacing: '0.08em' }} data-testid="code-verification" aria-label={`Code ${fait.code_verification}`}>
            {fait.code_verification}
          </p>
          <p className="note">
            L’administrateur ne le voit pas dans l’application : il doit le saisir. S’il ne vous reconnaît pas ou si vous n’avez rien demandé,
            révoquez la clé ci-dessous.
          </p>
          <div className="ligne">
            <button type="button" className="btn petit" onClick={() => setFait(null)}>
              J’ai transmis le code
            </button>
          </div>
        </div>
      )}
      {indispo && !fait && <p className="note">{indispo}</p>}
    </section>
  );
}

// ---------------------------------------------------------------- mes clés

function MesCles() {
  const { client, moi } = useEkera();
  const relire = useRelireCles();
  const cles = useQuery({ queryKey: ['mes-cles', moi.sujet], queryFn: () => client.mesCles() });
  const { occupe, erreur, executer } = useAction();
  const [revocation, setRevocation] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  // Ajout avec carence : la clé nouvelle n'est utilisable qu'à `active_le` ; d'ici là, seules les clés déjà actives valident.
  const [carence, setCarence] = useState<{ cle: VueCle; etat: EtatCle } | null>(null);
  const actives = moi.webauthn?.cles_actives ?? 0;

  async function ajouter(cle: VueCle) {
    setMessage(null);
    setCarence(null);
    const r = await executer(`ajout:${cle.cle_id}`, async () => {
      const defi = await client.optionsAjout(cle.cle_id);
      const assertion = await gesteAssertion(defi.options);
      return client.ajouter(cle.cle_id, defi.defi_id, assertion);
    });
    if (r) {
      if (r.etat === 'active') setMessage(`La clé « ${cle.nom} » est active : vous avez prouvé détenir une clé déjà active.`);
      else setCarence({ cle, etat: r });
      await relire();
    }
  }

  async function revoquer(cle: VueCle, motif: string) {
    setMessage(null);
    const r = await executer(`revocation:${cle.cle_id}`, () => client.revoquerMaCle(cle.cle_id, motif));
    if (r) {
      setRevocation(null);
      setCarence(null);
      setMessage(`La clé « ${cle.nom} » est révoquée. Elle reste dans l’historique et ne sert plus.`);
      await relire();
    }
  }

  return (
    <section className="panneau pile" aria-labelledby="t-mes-cles">
      <h2 id="t-mes-cles">Mes clés</h2>
      {cles.isPending && <Chargement quoi="de vos clés" />}
      {cles.error && <Erreur erreur={cles.error} titre="Impossible de lire vos clés." />}
      {cles.data && cles.data.length === 0 && <p className="note">Aucune clé enregistrée.</p>}
      {cles.data?.some((c) => cleInutilisable(c.etat)) && (
        <p className="encadre erreur" role="alert" data-testid="cle-invalide-alerte">
          <b>Une de vos clés est inutilisable.</b> Ses données ont été modifiées en dehors de l’application : Ekera ne s’en sert plus et vos administrateurs
          sont prévenus. Si ce n’est pas normal, prévenez votre RSSI. Enregistrez une nouvelle clé (« Enregistrer une clé de sécurité » ci-dessus) :
          une clé inutilisable ne se répare pas et ne sert jamais à valider.
        </p>
      )}
      {carence && (
        <div className="encadre attention pile" style={{ gap: 8 }} role="status" data-testid="ajout-carence">
          <p>
            <b>Clé « {carence.cle.nom} » ajoutée, en délai de sécurité.</b> Elle ne pourra valider qu’à partir du{' '}
            <b>{carence.etat.active_le ? dateFr(carence.etat.active_le) : 'terme du délai de sécurité'}</b>. D’ici là, votre clé déjà active reste seule
            utilisable pour valider une écriture.
          </p>
          <p>
            Vous et vos administrateurs avez été alertés par courriel. Si vous n’avez pas demandé cet ajout, annulez-le tout de suite : la révocation
            de la clé nouvelle se fait sans geste.
          </p>
          <div className="ligne">
            <button type="button" className="btn petit" disabled={!!occupe} onClick={() => setRevocation(carence.cle.cle_id)}>
              Annuler cet ajout (révoquer la clé)…
            </button>
            <button type="button" className="btn petit" onClick={() => setCarence(null)}>
              J’ai compris
            </button>
          </div>
          {revocation === carence.cle.cle_id && (
            <FormulaireMotif
              libelle="Motif de l’annulation"
              bouton="Confirmer l’annulation (révocation)"
              occupe={occupe === `revocation:${carence.cle.cle_id}`}
              surConfirmer={(m) => void revoquer(carence.cle, m)}
              surAnnuler={() => setRevocation(null)}
              aide="L’annulation est la révocation de la clé nouvelle : immédiate, sans geste ; la clé reste dans l’historique."
            />
          )}
        </div>
      )}
      {cles.data && cles.data.length > 0 && (
        <div className="tableau-defile">
          <table data-testid="mes-cles">
            <caption>Vos clés de sécurité</caption>
            <thead>
              <tr>
                <th scope="col">Nom</th>
                <th scope="col">Modèle</th>
                <th scope="col">État</th>
                <th scope="col">Demandée le</th>
                <th scope="col">Dernier usage</th>
                <th scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {cles.data.map((c) => (
                <tr key={c.cle_id} data-testid={`cle-${c.cle_id}`}>
                  <th scope="row">{c.nom}</th>
                  <td>{c.modele}</td>
                  <td>
                    <Etiquette ton={c.etat === 'active' ? 'ok' : c.etat === 'en_attente' || cleInutilisable(c.etat) ? 'attention' : 'neutre'}>
                      {etatAffiche(c.etat, c.active_le, c.voie)}
                    </Etiquette>
                    {cleEnCarence(c) && c.active_le && (
                      <div className="note">
                        Active le {dateFr(c.active_le)}
                        {actives > 0 ? ' ; d’ici là, seules vos clés déjà actives valident.' : '.'}
                      </div>
                    )}
                    {cleInutilisable(c.etat) && (
                      <div className="note" data-testid={`invalide-${c.cle_id}`}>
                        Données altérées : cette clé ne sert plus et n’est jamais proposée à la validation. Enregistrez-en une nouvelle.
                      </div>
                    )}
                    {c.voie && <div className="note">{LIBELLE_VOIE_CLE[c.voie] ?? c.voie}</div>}
                    {c.etat === 'en_attente' && !c.active_le && (
                      <div className="note">
                        Code à lire à un administrateur : <span className="num" data-testid={`code-${c.cle_id}`}>{c.code_verification}</span>
                      </div>
                    )}
                  </td>
                  <td>{dateFr(c.cree_le)}</td>
                  <td>{c.derniere_utilisation_le ? dateFr(c.derniere_utilisation_le) : '—'}</td>
                  <td>
                    <div className="ligne">
                      {c.etat === 'en_attente' && !c.active_le && actives > 0 && (
                        <button type="button" className="btn petit" disabled={!!occupe} onClick={() => void ajouter(c)}>
                          {occupe === `ajout:${c.cle_id}` ? 'Touchez votre clé…' : 'Activer avec ma clé active'}
                        </button>
                      )}
                      {(c.etat === 'active' || c.etat === 'en_attente') && (
                        <button type="button" className="btn petit" disabled={!!occupe} onClick={() => setRevocation(c.cle_id)}>
                          {cleEnCarence(c) && c.voie === 'ajout' ? 'Annuler l’ajout…' : 'Révoquer…'}
                        </button>
                      )}
                    </div>
                    {revocation === c.cle_id && carence?.cle.cle_id !== c.cle_id && (
                      <FormulaireMotif
                        libelle="Motif de la révocation"
                        bouton="Confirmer la révocation"
                        occupe={occupe === `revocation:${c.cle_id}`}
                        surConfirmer={(m) => void revoquer(c, m)}
                        surAnnuler={() => setRevocation(null)}
                        aide="La révocation est immédiate et sans geste (une clé perdue doit pouvoir être coupée tout de suite) ; la clé reste dans l’historique."
                      />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <MessageErreur erreur={erreur} titre="L’opération n’a pas abouti." />
      {message && (
        <p className="encadre ok" role="status" data-testid="message-cles">
          {message}
        </p>
      )}
    </section>
  );
}

// ---------------------------------------------------------------- administration

function ClesDuClient() {
  const { client } = useEkera();
  const [etat, setEtat] = useState('en_attente');
  const cles = useQuery({ queryKey: ['cles-client', etat], queryFn: () => client.clesDuClient(etat) });
  const idEtat = useId();
  return (
    <section className="panneau pile" aria-labelledby="t-cles-client" data-testid="cles-client">
      <h2 id="t-cles-client">Clés à approuver (administration)</h2>
      <p className="note">
        Pour approuver la clé de quelqu’un, appelez cette personne (ou voyez-la) et demandez-lui de vous <b>lire</b> le code de vérification
        affiché dans son écran « Mes clés ». Ne le cherchez pas ici : Ekera ne vous le montre pas, pour qu’une clé demandée avec un jeton volé
        ne soit pas approuvée sans que la vraie personne le sache. Vous confirmez ensuite par un geste sur votre propre clé.
      </p>
      <div className="champ" style={{ maxWidth: 260 }}>
        <label htmlFor={idEtat}>Clés à afficher</label>
        <select id={idEtat} value={etat} onChange={(e) => setEtat(e.target.value)}>
          <option value="en_attente">En attente d’approbation</option>
          <option value="active">Actives</option>
          <option value="revoquee">Révoquées</option>
          <option value="refusee">Refusées</option>
          <option value="expiree">Expirées</option>
          <option value="invalide">Inutilisables (état altéré)</option>
        </select>
      </div>
      {cles.isPending && <Chargement quoi="des clés de l’entreprise" />}
      {cles.error && <Erreur erreur={cles.error} titre="Impossible de lire les clés de l’entreprise." />}
      {cles.data && cles.data.length === 0 && <p className="note">Aucune clé dans cet état.</p>}
      {cles.data && cles.data.length > 0 && (
        <ul className="liste-nue pile" style={{ gap: 12 }}>
          {cles.data.map((c) => (
            <LigneCleClient key={c.cle_id} cle={c} />
          ))}
        </ul>
      )}
    </section>
  );
}

function LigneCleClient({ cle }: { cle: VueCleAdmin }) {
  const { client, moi } = useEkera();
  const relire = useRelireCles();
  const { occupe, erreur, executer } = useAction();
  const [code, setCode] = useState('');
  const [motif, setMotif] = useState<'refus' | 'revocation' | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const idCode = useId();
  const soi = cle.personne.auteur === (moi.nom ?? moi.sujet);
  const peutApprouver = !!moi.webauthn?.peut_approuver;
  const codeOk = codeVerificationValide(code);

  async function approuver(e: FormEvent) {
    e.preventDefault();
    setMessage(null);
    const r = await executer('approbation', async () => {
      const defi = await client.optionsApprobation(cle.cle_id);
      // le geste de l'administrateur : il touche SA clé ; le code lu par la personne est jugé par l'API après le geste
      const assertion = await gesteAssertion(defi.options);
      return client.approuver(cle.cle_id, defi.defi_id, assertion, code);
    });
    if (r) {
      setCode('');
      setMessage(
        r.active_le
          ? `Clé approuvée. Elle sera active le ${dateFr(r.active_le)} (délai de sécurité) ; la personne est alertée et peut la révoquer d’ici là.`
          : 'Clé approuvée.',
      );
      await relire();
    }
  }

  async function avecMotif(m: string) {
    const nature = motif;
    setMessage(null);
    const r = await executer(nature ?? 'motif', () => (nature === 'refus' ? client.refuserCle(cle.cle_id, m) : client.revoquerCle(cle.cle_id, m)));
    if (r) {
      setMotif(null);
      setMessage(nature === 'refus' ? 'Demande refusée.' : 'Clé révoquée : elle reste dans l’historique et ne sert plus.');
      await relire();
    }
  }

  return (
    <li className="encadre info pile" style={{ gap: 8 }} data-testid={`cle-client-${cle.cle_id}`}>
      <p>
        <b>{cle.nom}</b> · {cle.personne.auteur} · {cle.modele} · demandée le {dateFr(cle.cree_le)} ·{' '}
        <Etiquette ton={cle.etat === 'active' ? 'ok' : cle.etat === 'en_attente' || cleInutilisable(cle.etat) ? 'attention' : 'neutre'}>
          {etatAffiche(cle.etat, cle.active_le)}
        </Etiquette>
        {cle.etat === 'en_attente' && cle.active_le ? ` · active le ${dateFr(cle.active_le)}` : ''}
      </p>
      {cleInutilisable(cle.etat) && (
        <p className="encadre erreur" data-testid={`cle-client-invalide-${cle.cle_id}`}>
          Les données de cette clé ont été modifiées en dehors de l’application (scellé rompu) : elle est inutilisable et ne peut ni être approuvée ni valider.
          Vérifiez l’accès à la base avec votre RSSI ; {cle.personne.auteur} doit enregistrer une nouvelle clé.
        </p>
      )}
      {cle.etat === 'en_attente' && !cle.active_le && (
        <form className="pile" style={{ gap: 8 }} onSubmit={(e) => void approuver(e)}>
          {soi ? (
            <p className="note">C’est votre propre demande : un autre administrateur doit l’approuver.</p>
          ) : !peutApprouver ? (
            <p className="note">Pour approuver, il vous faut le rôle d’administration et une clé active.</p>
          ) : (
            <>
              <div className="champ" style={{ maxWidth: 320 }}>
                <label htmlFor={idCode}>
                  Code de vérification lu par {cle.personne.auteur} <span className="aide">(« XXXX-XXXX », lu à voix haute ou au téléphone)</span>
                </label>
                <input
                  id={idCode}
                  type="text"
                  inputMode="text"
                  autoComplete="off"
                  spellCheck={false}
                  value={code}
                  maxLength={9}
                  onChange={(e) => setCode(normaliserCodeVerification(e.target.value))}
                />
              </div>
              <div className="ligne">
                <button type="submit" className="btn principal" disabled={!!occupe || !codeOk || !!indisponibiliteNavigateur()}>
                  {occupe === 'approbation' ? 'Touchez votre clé de sécurité…' : 'Approuver avec ma clé'}
                </button>
              </div>
            </>
          )}
        </form>
      )}
      <div className="ligne">
        {cle.etat === 'en_attente' && !soi && (
          <button type="button" className="btn petit" disabled={!!occupe} onClick={() => setMotif('refus')}>
            Refuser…
          </button>
        )}
        {(cle.etat === 'active' || cle.etat === 'en_attente') && (
          <button type="button" className="btn petit" disabled={!!occupe} onClick={() => setMotif('revocation')}>
            Révoquer…
          </button>
        )}
      </div>
      {motif && (
        <FormulaireMotif
          libelle={motif === 'refus' ? 'Motif du refus' : 'Motif de la révocation'}
          bouton={motif === 'refus' ? 'Confirmer le refus' : 'Confirmer la révocation'}
          occupe={occupe === motif}
          surConfirmer={(m) => void avecMotif(m)}
          surAnnuler={() => setMotif(null)}
        />
      )}
      <MessageErreur erreur={erreur} titre="L’opération n’a pas abouti." />
      {message && (
        <p className="encadre ok" role="status">
          {message}
        </p>
      )}
    </li>
  );
}
